import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { evidenceFor, rubricSkill } from '../../store/selectors'
import { formatRelative } from '../../lib/dates'
import {
  LEVEL_LABELS,
  assessmentHints,
  clampLevel,
  domainColor,
  domainLabel,
  frameworkSkill,
  levelDescriptor,
  nextLevelDescriptor,
  prerequisites,
} from '../../lib/rubric'
import { LevelBar } from './LevelBar'
import { TrendBadge, describeTrend } from './TrendBadge'
import { ConfidenceDot, explainConfidence } from './ConfidenceDot'
import { OverrideControl } from './OverrideControl'
import { EvidenceTrail } from './EvidenceTrail'

/**
 * One skill, in full.
 *
 * A level is only worth showing if it means something, so the descriptor —
 * the concrete, observable behaviour that defines this rung for this skill —
 * is the headline, and the number is a label on it. Next comes the rung above,
 * because the useful question is always "what does the next step look like",
 * never "how far along is she".
 *
 * The order is deliberate: what it means, how sure we are, what is underneath
 * it, how to check it, your call, then the evidence. Belief before numbers.
 */
export function SkillDetail({
  domainKey,
  skillKey,
  onOpenSkill,
  childName,
}: {
  domainKey: string
  skillKey: string
  onOpenSkill?: (domainKey: string, skillKey: string) => void
  childName?: string
}) {
  const skill = useAuroraStore((s: any) => rubricSkill(s, domainKey, skillKey))
  const evidence = useAuroraStore((s: any) => evidenceFor(s, domainKey, skillKey))
  const definition = frameworkSkill(domainKey, skillKey)

  const name = definition?.name ?? skill?.name ?? skillKey
  const level = clampLevel(skill?.level)
  const current = levelDescriptor(domainKey, skillKey, level)
  const next = nextLevelDescriptor(domainKey, skillKey, level)
  const prereqs = prerequisites(domainKey, skillKey)
  const hints = assessmentHints(domainKey, skillKey)
  const direct = evidence.filter((e: any) => e.direct)
  const lastObservedAt = evidence[0]?.at

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  return (
    <article
      className="skilldetail"
      style={{ '--domain-color': domainColor(domainKey, 'var(--accent)') } as React.CSSProperties}
    >
      <header className="skilldetail-head reveal" style={{ '--i': 0 } as React.CSSProperties}>
        <span className="pill pill-domain">{domainLabel(domainKey)}</span>
        <h2 className="skilldetail-name">{name}</h2>
        {definition?.description && (
          <p className="skilldetail-desc">{definition.description}</p>
        )}
      </header>

      {/* ── where she is ─────────────────────────────────────── */}
      <section className="skilldetail-level reveal" style={{ '--i': 1 } as React.CSSProperties}>
        {level ? (
          <>
            <div className="levelmark">
              <span className="levelmark-num tnum">{level}</span>
              <span className="levelmark-name">{LEVEL_LABELS[level]}</span>
              {skill?.parentOverride && <span className="levelmark-yours">Your call</span>}
            </div>
            <LevelBar
              level={level}
              domainKey={domainKey}
              confidence={skill?.confidence}
              size="lg"
              label={`Level ${level} of 5`}
            />

            {current ? (
              <div className="descriptor">
                <h3 className="descriptor-title">What level {level} means here</h3>
                <p className="descriptor-text">{current.descriptor}</p>
                {current.exemplars.length > 0 && (
                  <ul className="descriptor-examples">
                    {current.exemplars.map((example) => (
                      <li key={example}>{example}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <p className="skilldetail-gap">
                This skill came from Sapling rather than the built-in framework, so there is no
                written description of each level for it yet. Ask what level {level} means here and
                it will tell you.
              </p>
            )}

            {next && (
              <div className="descriptor is-next">
                <h3 className="descriptor-title">What comes next</h3>
                <p className="descriptor-text">{next.descriptor}</p>
              </div>
            )}
          </>
        ) : (
          <div className="skilldetail-unmeasured">
            <p className="skilldetail-unmeasured-title">Not looked at yet</p>
            <p className="skilldetail-unmeasured-body">
              Nothing has been logged for this, so there is no level, which says nothing about
              what {childName ?? 'she'} can do. Here is what it looks like at each step, and how
              to find out.
            </p>
            <ol className="ladder">
              {[1, 2, 3, 4, 5].map((rung) => {
                const rungDescriptor = levelDescriptor(domainKey, skillKey, rung)
                if (!rungDescriptor) return null
                return (
                  <li key={rung} className="ladder-step">
                    <span className="ladder-num tnum">{rung}</span>
                    <span className="ladder-text">{rungDescriptor.descriptor}</span>
                  </li>
                )
              })}
            </ol>
          </div>
        )}
      </section>

      {/* ── how sure, and which way ──────────────────────────── */}
      {level && (
        <section className="skilldetail-reads reveal" style={{ '--i': 2 } as React.CSSProperties}>
          <div className="read">
            <div className="read-head">
              <h3 className="section-title">Direction</h3>
              <TrendBadge trend={skill?.trend} size="sm" />
            </div>
            <p className="read-body">
              {describeTrend(skill?.trend) ??
                'Not enough logged yet to say which way this is moving.'}
            </p>
          </div>

          <div className="read">
            <div className="read-head">
              <h3 className="section-title">How sure</h3>
              <ConfidenceDot
                confidence={skill?.confidence}
                observationCount={direct.length}
                lastObservedAt={lastObservedAt}
                size="sm"
              />
            </div>
            <p className="read-body">
              {typeof skill?.confidence === 'number'
                ? explainConfidence(skill.confidence, direct.length, lastObservedAt)
                : 'Sapling has not put a confidence on this yet.'}
            </p>
          </div>
        </section>
      )}

      {skill?.note && (
        <section className="skilldetail-note reveal" style={{ '--i': 3 } as React.CSSProperties}>
          <h3 className="section-title">Sapling’s note</h3>
          <p className="skilldetail-note-body">{skill.note}</p>
        </section>
      )}

      {/* ── what sits underneath ─────────────────────────────── */}
      {prereqs.length > 0 && (
        <section className="skilldetail-prereqs reveal" style={{ '--i': 4 } as React.CSSProperties}>
          <h3 className="section-title">Rests on</h3>
          <p className="prereq-lead">
            When this one will not move, the answer is often one step below it.
          </p>
          <ul className="prereq-list">
            {prereqs.map((prereq) => (
              <li key={prereq.id}>
                <PrereqChip prereq={prereq} onOpenSkill={onOpenSkill} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── how to check ─────────────────────────────────────── */}
      {hints.length > 0 && (
        <section className="skilldetail-hints reveal" style={{ '--i': 5 } as React.CSSProperties}>
          <h3 className="section-title">How to check where she is</h3>
          <ul className="hint-list">
            {hints.map((hint) => (
              <li key={hint} className="hint">
                {hint}
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="reveal" style={{ '--i': 6 } as React.CSSProperties}>
        <OverrideControl domainKey={domainKey} skillKey={skillKey} skill={skill} />
      </div>

      <div className="reveal" style={{ '--i': 7 } as React.CSSProperties}>
        <EvidenceTrail
          domainKey={domainKey}
          skillKey={skillKey}
          skillName={name}
          childName={childName}
        />
      </div>

      <div className="skilldetail-actions reveal" style={{ '--i': 8 } as React.CSSProperties}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => ask(`What should we do next for ${name.toLowerCase()}?`)}
        >
          What should we try?
        </button>
        {skill?.trend === 'stalled' && (
          <button
            className="btn btn-quiet btn-sm"
            onClick={() =>
              ask(`${name} has not moved. What is a completely different way in?`)
            }
          >
            Try a different angle
          </button>
        )}
      </div>
    </article>
  )
}

/**
 * A prerequisite is only useful if you can go and look at it, so each one
 * carries its own level and opens its own page.
 */
function PrereqChip({
  prereq,
  onOpenSkill,
}: {
  prereq: { id: string; name: string; domainKey?: string }
  onOpenSkill?: (domainKey: string, skillKey: string) => void
}) {
  const skillKey = prereq.id.slice(prereq.id.lastIndexOf('.') + 1)
  const state = useAuroraStore((s: any) => rubricSkill(s, prereq.domainKey, skillKey))
  const level = clampLevel(state?.level)
  const canOpen = !!onOpenSkill && !!prereq.domainKey

  const body = (
    <>
      <span className="prereq-name">{prereq.name}</span>
      {level ? (
        <span className="prereq-level tnum">{level}</span>
      ) : (
        <span className="prereq-level is-blank">—</span>
      )}
    </>
  )

  if (!canOpen) return <span className="prereq">{body}</span>

  return (
    <button
      className="prereq is-link"
      onClick={() => onOpenSkill!(prereq.domainKey!, skillKey)}
      style={{ '--domain-color': domainColor(prereq.domainKey) } as React.CSSProperties}
      aria-label={level ? `${prereq.name}, level ${level} of 5` : `${prereq.name}, not looked at yet`}
    >
      {body}
    </button>
  )
}
